import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { getActiveSpace } from 'spaces/store/selectors';
import { getActiveDocument } from 'documents/store/documents/selectors';
import PdfViewer from 'documents/components/PdfViewer/PdfViewer';
import MainTitle from 'base/components/Title3';
import ErrorBoundary from 'base/components/ErrorBoundary';
import { FullScreenModalHeader } from 'modal/components/ModalHeader/ModalHeader';
import HeaderMessage from 'base/components/HeaderMessage';
import { isSafari } from 'utils/browser';
import OfflineAnnotationStore from '../../util/OfflineAnnotationStore';
import OfflineFileStore from '../../util/OfflineFileStore';
import { addOfflineStorageErrorId } from '../../store/actions';

class OfflineDocumentViewModal extends React.Component {
  state = {
    isLoaded: false,
    hasError: false,
    pdf: null,
    instantJson: null,
    errorMessage: null,
  };

  componentDidMount() {
    this.loadPdf();
  }

  /**
   * Fetches the PDF file and annotations from the offline store
   */
  loadPdf = () => {
    const { document, addOfflineStorageErrorId } = this.props;

    Promise.all([
      OfflineFileStore.get(document.id.toString()),
      OfflineAnnotationStore.get(document.id.toString()),
    ])
      .then((values) => {
        const [pdf, instantJson] = values;
        if (!pdf) {
          throw Error('Could not retrieve file from offline storage');
        }
        this.setState({
          pdf,
          instantJson,
          isLoaded: true,
        });
      })
      .catch((error) => {
        addOfflineStorageErrorId(document.id);
        this.setState({
          hasError: true,
          errorMessage: error.message,
        });
      });
  };

  render() {
    const { document } = this.props;
    const {
      pdf, instantJson, isLoaded, hasError, errorMessage,
    } = this.state;
    const ErrorFallback = () => <div>The file viewer does not support documents of this type.</div>;

    const viewer = document && isLoaded
      ? (
        <ErrorBoundary fallback={ErrorFallback}>
          <PdfViewer
            pdf={pdf}
            instantJson={instantJson}
            documentId={document.id}
            style={{
              width: 'auto',
              flex: '1 1 auto',
            }}
            fileName={document.file.mime === 'application/pdf'
              ? document.file.filename
              : document.file.pdfCopyFilename}
          />
        </ErrorBoundary>
      )
      : (
        <div>
              Loading...
        </div>
      );

    const error = (
      <div style={{ padding: '50px' }}>
        <h1>Error</h1>
        <p>{errorMessage}</p>
      </div>
    );

    return (
      <>
        <FullScreenModalHeader>
          <MainTitle>
            {document ? document.name : 'Loading..'}
          </MainTitle>
        </FullScreenModalHeader>
        {hasError ? error : viewer}
        {isSafari() && (
          <HeaderMessage
            type="warning"
            title="Go online within the next 7 days to save your changes"
            message={(
              <div>
                Safari will only keep your offline changes
                for the next 7 days for data privacy reasons.
              </div>
            )}
            style={{
              flexGrow: 0,
            }}
          />
        )}
      </>
    );
  }
}

OfflineDocumentViewModal.propTypes = {
  document: PropTypes.objectOf(PropTypes.any).isRequired,
  addOfflineStorageErrorId: PropTypes.func.isRequired,
};


const mapStateToProps = (state) => ({
  space: getActiveSpace(state),
  document: getActiveDocument(state),
});

const mapDispatchToProps = {
  addOfflineStorageErrorId,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(OfflineDocumentViewModal);
